import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EventSpeaker, EventSpeakerInsert } from '@/types/events';
import { toast } from 'sonner';

const syncSpeakerToContacts = async (
  brandId: string,
  speaker: { name: string; email?: string | null; phone?: string | null }
): Promise<string | null> => {
  if (!speaker.email) return null;
  
  const nameParts = speaker.name.trim().split(/\s+/);
  const firstName = nameParts[0] || speaker.name;
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  
  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: speaker.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: speaker.phone || null,
      p_module: ['Events'],
      p_activity: ['speaker'],
      p_organization: null,
      p_title: null,
      p_user_id: null,
    });
    
    if (error) {
      console.error('Error syncing speaker to contacts:', error);
      return null;
    }
    
    return contactId;
  } catch (error) {
    console.error('Error syncing speaker to contacts:', error);
    return null;
  }
};

export function useEventSpeakers(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data: speakers = [], isLoading } = useQuery({
    queryKey: ['event-speakers', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data, error } = await supabase
        .from('os_events_speakers')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as EventSpeaker[];
    },
    enabled: !!brandId,
  });

  const createSpeaker = useMutation({
    mutationFn: async (payload: EventSpeakerInsert) => {
      // Sync to contacts first
      const contactId = await syncSpeakerToContacts(payload.brand_id, {
        name: payload.name,
        email: payload.email,
        phone: null,
      });
      
      const { data, error } = await supabase
        .from('os_events_speakers')
        .insert([{ ...payload, contact_id: contactId }])
        .select()
        .single();
      if (error) throw error;
      return data as EventSpeaker;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-speakers', data.brand_id] });
      toast.success('Speaker created');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to create speaker'),
  });

  const updateSpeaker = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<EventSpeaker> & { id: string }) => {
      // Get current speaker for brand_id and contact sync
      const { data: currentSpeaker } = await supabase
        .from('os_events_speakers')
        .select('brand_id, name, email')
        .eq('id', id)
        .single();
      
      let contactId: string | null = null;
      if (currentSpeaker) {
        contactId = await syncSpeakerToContacts(currentSpeaker.brand_id, {
          name: updates.name || currentSpeaker.name,
          email: updates.email || currentSpeaker.email,
          phone: null,
        });
      }
      
      const { data, error } = await supabase
        .from('os_events_speakers')
        .update({ ...updates, ...(contactId ? { contact_id: contactId } : {}) })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as EventSpeaker;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-speakers', data.brand_id] });
      toast.success('Speaker updated');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to update speaker'),
  });

  const deleteSpeaker = useMutation({
    mutationFn: async (speakerId: string) => {
      const { error } = await supabase
        .from('os_events_speakers')
        .update({ is_active: false })
        .eq('id', speakerId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (brandId) queryClient.invalidateQueries({ queryKey: ['event-speakers', brandId] });
      toast.success('Speaker removed');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to remove speaker'),
  });

  const importFromInstructors = useMutation({
    mutationFn: async (instructorIds: string[]) => {
      if (!brandId) throw new Error('Brand ID required');
      const { data: instructors, error: fetchError } = await supabase
        .from('os_lms_instructors')
        .select('*')
        .in('id', instructorIds)
        .eq('is_active', true);
      if (fetchError) throw fetchError;
      if (!instructors?.length) throw new Error('No instructors found');
      
      // Sync each instructor to contacts and build rows with contact_id
      const rows: (EventSpeakerInsert & { contact_id?: string | null })[] = await Promise.all(
        instructors.map(async (instructor) => {
          const contactId = await syncSpeakerToContacts(brandId, {
            name: instructor.name,
            email: instructor.email,
            phone: null,
          });
          return {
            brand_id: brandId,
            name: instructor.name,
            slug: instructor.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
            title: instructor.title ?? null,
            bio: instructor.bio ?? null,
            email: instructor.email ?? null,
            image_url: instructor.image_url ?? null,
            social_links: instructor.social_links as Record<string, string> | null,
            is_active: true,
            contact_id: contactId,
          };
        })
      );
      
      const { data, error } = await supabase
        .from('os_events_speakers')
        .insert(rows)
        .select();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-speakers', brandId] });
      toast.success(`${data?.length ?? 0} speaker(s) imported from instructors`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to import from instructors'),
  });

  const importFromAuthors = useMutation({
    mutationFn: async (authorIds: string[]) => {
      if (!brandId) throw new Error('Brand ID required');
      const { data: authors, error: fetchError } = await supabase
        .from('os_article_authors')
        .select('*')
        .in('id', authorIds)
        .eq('is_active', true);
      if (fetchError) throw fetchError;
      if (!authors?.length) throw new Error('No authors found');
      
      // Sync each author to contacts and build rows with contact_id
      const rows: (EventSpeakerInsert & { contact_id?: string | null })[] = await Promise.all(
        authors.map(async (author) => {
          const contactId = await syncSpeakerToContacts(brandId, {
            name: author.name,
            email: author.email,
            phone: null,
          });
          return {
            brand_id: brandId,
            name: author.name,
            slug: (author.slug ?? author.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) as string,
            title: author.title ?? null,
            bio: author.bio ?? null,
            email: author.email ?? null,
            image_url: author.avatar_url ?? null,
            social_links: author.social_links as Record<string, string> | null,
            is_active: true,
            contact_id: contactId,
          };
        })
      );
      
      const { data, error } = await supabase
        .from('os_events_speakers')
        .insert(rows)
        .select();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-speakers', brandId] });
      toast.success(`${data?.length ?? 0} speaker(s) imported from authors`);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to import from authors'),
  });

  return {
    speakers,
    isLoading,
    createSpeaker,
    updateSpeaker,
    deleteSpeaker,
    importFromInstructors,
    importFromAuthors,
  };
}
